/**
 * Montagem de buffers de faixa a partir de segmentos gravados.
 *
 * Regra central de sincronização: cada segmento sabe em qual posição da
 * linha do tempo da música ele começa (`position`). Assim, punch-in,
 * regravação de trecho e comping nunca "empurram" o resto da gravação.
 */
import type { CompingRegion, RecordedSegment, Take } from '../types.js';

export interface MergeOptions {
  /** Posição inicial da saída (segundos na linha do tempo). Default: 0. */
  start?: number;
  /** Duração total da saída. Default: até o fim do último segmento. */
  duration?: number;
  sampleRate: number;
  /** Como resolver sobreposição entre segmentos: último ganha ou crossfade. */
  overlap?: 'last-wins' | 'crossfade';
  crossfadeMs?: number;
}

/** Converte um segmento (qualquer sample rate) para o sample rate alvo. */
export function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const outLen = Math.max(0, Math.round(input.length / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const src = i * ratio;
    const i0 = Math.floor(src);
    const i1 = Math.min(input.length - 1, i0 + 1);
    const f = src - i0;
    out[i] = input[i0] * (1 - f) + input[i1] * f;
  }
  return out;
}

/**
 * Junta segmentos em uma faixa contínua.
 * Retorna o Float32Array e o deslocamento (em segundos) do primeiro sample.
 */
export function mergeSegments(
  segments: RecordedSegment[],
  opts: MergeOptions
): { data: Float32Array; offset: number; duration: number } {
  const sr = opts.sampleRate;
  const list = [...segments].sort((a, b) => a.position - b.position);
  if (!list.length) return { data: new Float32Array(0), offset: 0, duration: 0 };

  const first = Math.max(0, list[0].position);
  const lastEnd = list.reduce((m, s) => Math.max(m, s.position + s.samples.length / s.sampleRate), 0);
  const duration = opts.duration != null ? Math.max(0, opts.duration) : Math.max(0, lastEnd - (opts.start ?? 0));
  const start = opts.start ?? 0;
  const length = Math.max(1, Math.round(duration * sr));
  const out = new Float32Array(length);
  const written = new Float32Array(length); // peso já escrito (para crossfade)
  const cf = Math.round(((opts.crossfadeMs ?? 25) / 1000) * sr);

  for (const seg of list) {
    const samples = seg.sampleRate === sr ? seg.samples : resampleLinear(seg.samples, seg.sampleRate, sr);
    const destStart = Math.round((seg.position - start) * sr);
    const overlapMode = opts.overlap ?? 'last-wins';
    for (let i = 0; i < samples.length; i++) {
      const d = destStart + i;
      if (d < 0 || d >= length) continue;
      if (overlapMode === 'crossfade' && written[d] > 0 && cf > 0) {
        // crossfade curto na borda de entrada do segmento novo
        const inFade = Math.min(1, i / Math.max(1, cf));
        out[d] = out[d] * (1 - inFade) + samples[i] * inFade;
        written[d] = 1;
      } else {
        out[d] = samples[i];
        written[d] = 1;
      }
    }
  }
  return { data: out, offset: first - start, duration: length / sr };
}

/** Aplica um mapa de comping: para cada região, usa o trecho do take indicado. */
export function applyComping(
  takes: Take[],
  regions: CompingRegion[],
  opts: MergeOptions
): { data: Float32Array; offset: number; duration: number } {
  if (!regions.length) return { data: new Float32Array(0), offset: 0, duration: 0 };
  const sr = opts.sampleRate;
  const sorted = [...regions].sort((a, b) => a.start - b.start);
  const end = sorted.reduce((m, r) => Math.max(m, r.end), 0);
  const start = opts.start ?? 0;
  const length = Math.max(1, Math.round((opts.duration ?? end - start) * sr));
  const out = new Float32Array(length);

  for (const region of sorted) {
    const take = takes.find((t) => t.id === region.takeId);
    if (!take) continue;
    const merged = mergeSegments(take.segments, { sampleRate: sr, start, duration: end - start });
    const a = Math.max(0, Math.round((region.start - start) * sr));
    const b = Math.min(length, Math.round((region.end - start) * sr));
    const fade = Math.round(0.01 * sr);
    for (let i = a; i < b; i++) {
      if (i >= merged.data.length) break;
      let g = 1;
      if (i - a < fade) g = (i - a) / fade;
      else if (b - i < fade) g = (b - i) / fade;
      out[i] = merged.data[i] * g;
    }
  }
  return { data: out, offset: 0, duration: length / sr };
}

/** Duração efetiva gravada de um take (soma dos segmentos). */
export function takeDuration(take: Take): number {
  return take.segments.reduce((s, seg) => s + seg.samples.length / seg.sampleRate, 0);
}

/** Recorta um trecho de um Float32Array (não destrutivo). */
export function sliceData(data: Float32Array, sampleRate: number, startSec: number, endSec: number): Float32Array {
  const a = Math.max(0, Math.round(startSec * sampleRate));
  const b = Math.min(data.length, Math.round(endSec * sampleRate));
  if (b <= a) return new Float32Array(0);
  return Float32Array.from(data.subarray(a, b));
}

/** Insere um trecho regravado dentro de uma faixa, preservando o resto. */
export function punchReplace(
  base: Float32Array,
  patch: Float32Array,
  sampleRate: number,
  positionSec: number,
  crossfadeMs = 15
): Float32Array {
  const out = Float32Array.from(base);
  const start = Math.round(positionSec * sampleRate);
  const cf = Math.round((crossfadeMs / 1000) * sampleRate);
  const needed = start + patch.length;
  const result = needed > out.length ? grow(out, needed) : out;
  for (let i = 0; i < patch.length; i++) {
    const d = start + i;
    if (d < 0) continue;
    const inFade = cf > 0 && i < cf ? i / cf : 1;
    const outFade = cf > 0 && patch.length - i < cf ? (patch.length - i) / cf : 1;
    const g = Math.min(inFade, outFade);
    const prev = result[d] ?? 0;
    result[d] = prev * (1 - g) + patch[i] * g;
  }
  return result;
}

function grow(data: Float32Array, length: number): Float32Array {
  const out = new Float32Array(length);
  out.set(data.subarray(0, Math.min(data.length, length)));
  return out;
}

/**
 * Cria um AudioBuffer mono a partir de Float32Array.
 * (Fábrica injetada para funcionar também com OfflineAudioContext.)
 */
export function toMonoBuffer(
  ctx: { sampleRate: number; createBuffer(c: number, l: number, sr: number): AudioBuffer },
  data: Float32Array,
  sampleRate?: number
): AudioBuffer {
  const sr = sampleRate ?? ctx.sampleRate;
  const buf = ctx.createBuffer(1, Math.max(1, data.length), sr);
  buf.copyToChannel(Float32Array.from(data), 0);
  return buf;
}

export function toStereoBuffer(
  ctx: { sampleRate: number; createBuffer(c: number, l: number, sr: number): AudioBuffer },
  left: Float32Array,
  right: Float32Array,
  sampleRate?: number
): AudioBuffer {
  const sr = sampleRate ?? ctx.sampleRate;
  const len = Math.max(1, Math.max(left.length, right.length));
  const buf = ctx.createBuffer(2, len, sr);
  buf.copyToChannel(Float32Array.from(left.subarray(0, len)), 0);
  buf.copyToChannel(Float32Array.from(right.subarray(0, len)), 1);
  return buf;
}

/** Concatena AudioBuffers mono (usado ao juntar chunks do MediaRecorder). */
export function concatMono(buffers: AudioBuffer[]): Float32Array {
  const total = buffers.reduce((s, b) => s + b.length, 0);
  const out = new Float32Array(total);
  let o = 0;
  for (const b of buffers) {
    out.set(b.getChannelData(0), o);
    o += b.length;
  }
  return out;
}
